'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ScrollArea } from '#pplx/frontend/components/ScrollArea.js';
import { animated, easings, useSpring } from '@react-spring/web';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
const SCROLL_FADE_PX = 24;
const SCROLL_CHROME_TRANSITION_MS = 150;
export const ScrollFadeContainer = memo(function ScrollFadeContainer({ children, topFadeOffsetPx = 0 }) {
    const scrollContainerRef = useRef(null);
    const rafRef = useRef(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const updateScrollState = useCallback(() => {
        const element = scrollContainerRef.current;
        if (!element)
            return;
        const newCanScrollUp = element.scrollTop > 0;
        const newCanScrollDown = element.scrollTop + element.clientHeight < element.scrollHeight - 1;
        setCanScrollUp(newCanScrollUp);
        setCanScrollDown(newCanScrollDown);
    }, []);
    const scheduleUpdate = useCallback(() => {
        if (rafRef.current !== null)
            return;
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            updateScrollState();
        });
    }, [updateScrollState]);
    useEffect(() => {
        const element = scrollContainerRef.current;
        if (!element)
            return;
        updateScrollState();
        element.addEventListener('scroll', scheduleUpdate, { passive: true });
        const observeScrollContent = (resizeObserver) => {
            resizeObserver.disconnect();
            resizeObserver.observe(element);
            Array.from(element.children).forEach((child) => resizeObserver.observe(child));
        };
        const resizeObserver = new ResizeObserver(scheduleUpdate);
        observeScrollContent(resizeObserver);
        const mutationObserver = new MutationObserver(() => {
            observeScrollContent(resizeObserver);
            scheduleUpdate();
        });
        mutationObserver.observe(element, { childList: true });
        return () => {
            element.removeEventListener('scroll', scheduleUpdate);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            if (rafRef.current !== null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [scheduleUpdate, updateScrollState]);
    const { topFadeProgress } = useSpring({
        topFadeProgress: canScrollUp ? 1 : 0,
        config: {
            duration: SCROLL_CHROME_TRANSITION_MS,
            easing: easings.easeInOutCubic,
        },
    });
    const topMaskImage = topFadeProgress.to((progress) => {
        const topOpacity = 1 - progress;
        const topFadeEndPx = topFadeOffsetPx + SCROLL_FADE_PX;
        return `linear-gradient(to bottom, rgba(0, 0, 0, ${topOpacity}) 0, rgba(0, 0, 0, ${topOpacity}) ${topFadeOffsetPx}px, black ${topFadeEndPx}px, black 100%)`;
    });
    const dividerOpacity = topFadeProgress.to((progress) => progress);
    const bottomMaskStyle = useMemo(() => {
        if (!canScrollDown)
            return undefined;
        const maskImage = `linear-gradient(to bottom, black 0, black calc(100% - ${SCROLL_FADE_PX}px), transparent 100%)`;
        return {
            maskImage,
            WebkitMaskImage: maskImage,
        };
    }, [canScrollDown]);
    return (_jsxs("div", { className: "relative flex-1 min-h-0", children: [_jsx(animated.div, { className: "h-full min-h-0 flex-1", style: { maskImage: topMaskImage, WebkitMaskImage: topMaskImage }, children: _jsx(ScrollArea, { className: "h-full min-h-0 flex-1", viewportRef: scrollContainerRef, viewportClassName: "h-full overflow-x-hidden overscroll-y-none", viewportContentLayout: "flexColumn", viewportStyle: bottomMaskStyle, children: children }) }), _jsx(animated.div, { className: "border-soft pointer-events-none absolute inset-x-0 z-10 h-px border-b", style: { opacity: dividerOpacity, top: topFadeOffsetPx } })] }));
});
//# sourceMappingURL=ScrollFadeContainer.js.map